import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, HeadphonesIcon, CheckSquareIcon, GiftIcon, ShieldCheckIcon, SparklesIcon, ZapIcon, CompassIcon, ShieldIcon } from 'lucide-react';
import { CountdownTimer } from './CountdownTimer';
import { useGeoPrice } from '../hooks/useGeoPrice';
const PURCHASE_URL = 'https://pay.hotmart.com/R104430088E?bid=1770999394323';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-40px'
  },
  transition: {
    duration: 0.3
  }
};
export function CTASection() {
  const {
    price,
    loading
  } = useGeoPrice();
  const displayPrice = loading ? '$27 USD' : price;
  return <section id="comprar" className="w-full bg-[#0f0529] py-16 md:py-24 px-5 relative overflow-hidden" data-id="element-42">
      {/* Background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/5 rounded-full blur-[100px] pointer-events-none" data-id="element-43" />

      <div className="max-w-4xl mx-auto relative z-10" data-id="element-44">
        <motion.div {...fadeIn} className="text-center mb-12" data-id="element-45">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-46">
            Todo lo que necesitas para transformar tu vida.
          </h2>
          <p className="text-lg text-gray-300" data-id="element-47">
            Un solo paquete. Un solo precio. Acceso inmediato.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 items-center bg-white/[0.03] border border-white/10 rounded-3xl p-6 md:p-8 backdrop-blur-sm" data-id="element-48">
          {/* Image Column */}
          <motion.div {...fadeIn} className="relative order-2 md:order-1" data-id="element-49">
            <img src="https://cdn.magicpatterns.com/uploads/qKirUDVmQuT63BJVzAPPXS/Super_Pack.png" alt="Super Pack Completo" className="w-full h-auto drop-shadow-2xl" loading="lazy" data-id="element-50" />
          </motion.div>

          {/* Content Column */}
          <motion.div {...fadeIn} transition={{
          delay: 0.1
        }} className="order-1 md:order-2 text-center md:text-left" data-id="element-51">
            {/* Urgency */}
            <div className="mb-6 flex justify-center md:justify-start" data-id="element-52">
              <CountdownTimer data-id="element-53" />
            </div>

            <h3 className="text-2xl font-bold text-white mb-4" data-id="element-54">
              Hábitos Atómicos Espirituales
            </h3>

            {/* Bonuses badge */}
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-1.5 mb-6" data-id="element-55">
              <GiftIcon className="w-4 h-4 text-amber-400" data-id="element-56" />
              <span className="text-amber-400 text-sm font-bold" data-id="element-57">
                +5 BONOS EXCLUSIVOS
              </span>
            </div>

            {/* Main Products */}
            <div className="space-y-3 mb-4" data-id="element-58">
              <IncludedItem icon={<BookOpenIcon className="w-4 h-4" data-id="element-60" />} text="Ebook — Guía completa + Plan 30 días" data-id="element-59" />
              <IncludedItem icon={<HeadphonesIcon className="w-4 h-4" data-id="element-62" />} text="Audiolibro — Escucha en cualquier momento" data-id="element-61" />
              <IncludedItem icon={<CheckSquareIcon className="w-4 h-4" data-id="element-64" />} text="Habit Tracker — Imprimible + Digital" data-id="element-63" />
            </div>

            {/* Bonus Items */}
            <div className="space-y-2 mb-8 pl-1" data-id="element-65">
              <p className="text-amber-500/60 text-xs font-semibold uppercase tracking-wider mb-2" data-id="element-66">
                Bonos incluidos:
              </p>
              <BonusItem icon={<SparklesIcon className="w-3 h-3" data-id="element-68" />} text="Frases de Poder" data-id="element-67" />
              <BonusItem icon={<CheckSquareIcon className="w-3 h-3" data-id="element-70" />} text="Habit Tracker Extra" data-id="element-69" />
              <BonusItem icon={<ZapIcon className="w-3 h-3" data-id="element-72" />} text="Reinicio Express — 7 Guiones" data-id="element-71" />
              <BonusItem icon={<CompassIcon className="w-3 h-3" data-id="element-74" />} text="Biblioteca de Señales — 21 Disparadores" data-id="element-73" />
              <BonusItem icon={<ShieldIcon className="w-3 h-3" data-id="element-76" />} text="Plan Anti-Abandono — 14 Días" data-id="element-75" />
            </div>

            {/* Price */}
            <div className="mb-8" data-id="element-77">
              <p className="text-gray-400 text-sm mb-2" data-id="element-78">
                Acceso inmediato y completo
              </p>
              <div className="flex items-center gap-3 justify-center md:justify-start mb-1" data-id="element-79">
                <span className="text-gray-500 line-through text-2xl" data-id="element-80">
                  $47.99 USD
                </span>
                <span className="bg-red-500/90 text-white text-xs font-black uppercase px-2.5 py-1 rounded-full" data-id="element-81">
                  -83% OFF
                </span>
              </div>
              <p className="text-4xl md:text-5xl font-bold text-white" data-id="element-82">
                $7.99 USD
              </p>
              <p className="text-amber-500/70 text-xs font-semibold mt-2" data-id="element-83">
                🔥 Super Promo — Solo por esta semana
              </p>
              <p className="text-gray-500 text-xs mt-1" data-id="element-84">
                Pago único · Sin suscripción
              </p>
            </div>

            {/* CTA */}
            <a href={PURCHASE_URL} className="cta-hero-glow block w-full bg-amber-500 hover:bg-amber-400 text-black font-black text-lg px-8 py-5 rounded-2xl transition-colors text-center" data-id="element-85">
              <span className="block" data-id="element-86">SUPER PROMO — $7.99 USD</span>
              <span className="block text-black/60 text-xs font-semibold mt-0.5" data-id="element-87">
                ⚡ Solo por esta semana
              </span>
            </a>

            <div className="mt-4 flex items-center justify-center md:justify-start gap-2 text-gray-500 text-xs" data-id="element-88">
              <ShieldCheckIcon className="w-3 h-3" data-id="element-89" />
              <span data-id="element-90">Pago 100% seguro · Acceso de por vida</span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="section-divider mt-16 md:mt-24" aria-hidden="true" data-id="element-91" />
    </section>;
}
function IncludedItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="flex items-center gap-3" data-id="element-92">
      <span className="text-amber-500" data-id="element-93">{icon}</span>
      <span className="text-gray-300 text-sm" data-id="element-94">{text}</span>
    </div>;
}
function BonusItem({
  icon,
  text
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return <div className="flex items-center gap-2" data-id="element-95">
      <span className="text-amber-500/60" data-id="element-96">{icon}</span>
      <span className="text-gray-400 text-xs" data-id="element-97">{text}</span>
    </div>;
}